import {
	AccountCircle,
	AssistantDirection,
	ConfirmationNumber,
	Domain,
	Email,
	LocationCity,
	LocationOn,
	Markunread,
	MyLocation,
	Phone,
	Search,
	Subtitles
} from '@mui/icons-material';
import {
	Autocomplete,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import DioceseTable from 'Components/AdminPanel/Diocese/DioceseTable';
import { useGlobalStyles } from 'Components/Common/global/global';
import { ColumnType, dioceseHead } from 'Components/Common/Tables/TableHeaders';
import { ContentSkeleton } from 'Components/Deposits/CreateEditDepositWithDonations/Content';
import ProvinceAutocomplete from 'Components/Province';
import useDebounce from 'Components/Reusable/hooks/debounce';
import AdvancedSearchModal, { Field } from 'Components/Reusable/Modals/AdvancedSearch/AdvancedSearchModal';
import { searchDioceseField } from 'Components/Reusable/Modals/AdvancedSearch/Mock';
import TrimTables from 'Components/Reusable/TrimTables/TrimTables';
import { Diocese, DioceseData, OrderType, Parish, StateType } from 'contexts/intialstates/Types';
import useDioceseActions from 'hooks/useDioceseActions';
import useParishesActions from 'hooks/useParishesActions';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export interface ParishValues {
	parishCode: string;
	parishName: string;
	pastor: string;
	contactName: string;
	streetline1: string;
	streetline2: string;
	city: string;
	province: string;
	provinceId?: number;
	postalCode: string;
	country: string;
	phone: string;
	fax: string;
	email: string;
	secondLanguage: string;
	useEmail: boolean;
	useEFile: boolean;
	noParishList: boolean;
	magazine: boolean;
	status: string;
	dioceseId?: number;
}

interface P {
	initial: ParishValues;
	setInitial: React.Dispatch<React.SetStateAction<ParishValues>>;
	formErrors: string[];
	setFormErrors: React.Dispatch<React.SetStateAction<string[]>>;
	reload?: boolean;
}

const CreateEditParish = ({ initial, setInitial, formErrors, setFormErrors, reload }: P) => {
	const global = useGlobalStyles();
	const { id } = useParams();
	const { getDioceses, getDioceseBySearch } = useDioceseActions();
	const { getParishesByID } = useParishesActions();

	const [parish, setParish] = useState<StateType<Parish>>({
		data: null,
		error: null,
		loading: false,
	});
	const [dioceseName, setDioceseName] = React.useState<string | undefined>('');
	const [searchDiocese, setSearchDiocese] = React.useState<StateType<Diocese[]>>({
		data: null,
		error: null,
		loading: false,
	});
	const [dioceseSearchTerm, setDioceseSearchTerm] = useState('');
	const dioceseDebouncedSearchTerm = useDebounce(dioceseSearchTerm, 1000);
	const [visibleSearchDiocese, setVisibleSearchDiocese] = useState(false);
	const [searchFieldsDiocese, setSearchFieldsDiocese] = useState<Field[]>(searchDioceseField);
	const [trimList, setTrimList] = useState<ColumnType[]>([]);
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(10);
	const [filterPayload, setFilterPayload] = useState<Array<any>>([]);
	const [orderType, setOrderType] = React.useState<OrderType>('asc');
	const [label, setLabel] = React.useState<string>('');
	const [orderBy, setOderBy] = React.useState<string>('');
	const [open, setOpen] = React.useState<any>({ state: false, type: '' });
	const [dioceses, setDioceses, ,] = React.useState<StateType<DioceseData>>({
		data: null,
		error: null,
		loading: false,
	});

	useEffect(() => {
		if (!dioceseDebouncedSearchTerm) return;
		getDioceseBySearch({ searchDiocese, setSearchDiocese, searchTerm: dioceseSearchTerm });
	}, [dioceseDebouncedSearchTerm]);
	useEffect(() => {
		getDioceses({
			archive: '&Filters=field=archived;value=false',
			setDioceses,
			dioceses,
			pageSize,
			page,
			orderBy,
			filterPayload,
		});
	}, [page, pageSize, orderBy, filterPayload.length]);
	useEffect(() => {
		if (!id) return;
		getParishesByID({ setParish, parish, id: parseInt(id) });
	}, [id, reload]);

	useEffect(() => {
		if (!parish?.data) return;
		const {
			parishCode,
			parishName,
			pastor,
			contactName,
			streetline1,
			streetline2,
			city,
			province,
			postalCode,
			country,
			phone,
			fax,
			email,
			secondLanguage,
			useEmail,
			useEFile,
			noParishList,
			magazine,
			status,
			diocese,
		} = parish?.data;
		setInitial({
			parishCode,
			parishName,
			pastor,
			contactName,
			streetline1,
			streetline2,
			city,
			province: province?.provinceName ?? '',
			provinceId: province?.id,
			postalCode,
			country,
			phone,
			fax,
			email,
			secondLanguage,
			useEmail,
			useEFile,
			noParishList,
			magazine,
			status,
			dioceseId: diocese?.id,
		});
		if (diocese) {
			setDioceseName(diocese.dioceseName);
		}
	}, [parish]);

	const handleChange = (field: string, value: string | number | boolean) => {
		setInitial({
			...initial,
			[field]: value,
		});
		if (formErrors.includes(field)) {
			setFormErrors(formErrors.filter((item) => item !== field));
		}
	};

	const handleVisibleSearchDiocese = () => {
		setVisibleSearchDiocese(!visibleSearchDiocese);
	};
	const handleSearchDiocese = () => {
		const payload: any = searchFieldsDiocese.map(({ key, value }) => {
			if (value) {
				return {
					field: key,
					query: key !== 'phone' ? value : null,
					value: key === 'phone' ? [value] : null,
					from: null,
					to: null,
				};
			}
		});

		setFilterPayload(payload.filter(Boolean));
	};
	const handleSelectRowDiocese = (row: any) => {
		const temp = { ...initial, dioceseId: parseInt(row.id) };
		setInitial(temp as any);
		setDioceseName(row.dioceseName);
		handleVisibleSearchDiocese();
		if (formErrors.includes('dioceseId')) {
			setFormErrors(formErrors.filter((item) => item !== 'dioceseId'));
		}
	};
	return (
		<>
			<Stack>
				{parish?.loading ? (
					<ContentSkeleton />
				) : (
					<>
						<Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2 }}>Basic Detail</Typography>
						<Divider />

						<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<ConfirmationNumber className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Parish Code
										</Typography>
									</Grid>
								</Grid>
								<Stack direction="column">
									<TextField
										size="small"
										fullWidth
										variant="outlined"
										placeholder="Parish Code"
										disabled
										value={initial.parishCode}
										className={global.disabledInput}
									/>
									<span style={{ color: 'var(--mainColor)', fontSize: 12, marginTop: 2 }}>
										This is an auto-generated code
									</span>
								</Stack>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Subtitles className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Parish Name
										</Typography>
									</Grid>
								</Grid>
								<Stack>
									<TextField
										size="small"
										fullWidth
										variant="outlined"
										placeholder="Parish Name"
										value={initial.parishName}
										onChange={(e) => {
											handleChange('parishName', e.target.value);
										}}
									/>
									{formErrors.includes('parishName') && (
										<span className={global.errorText}>Parish name is required</span>
									)}
								</Stack>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<ConfirmationNumber className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Pastor
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Pastor"
									value={initial.pastor}
									onChange={(e) => {
										handleChange('pastor', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Domain className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Diocese
										</Typography>
									</Grid>
								</Grid>
								<Stack alignItems="center" direction="row" spacing={1}>
									<Autocomplete
										freeSolo
										value={dioceseName ?? ''}
										loading={searchDiocese.loading}
										loadingText="loading..."
										size="small"
										fullWidth
										onChange={(event: React.SyntheticEvent<unknown>, val: any): void => {
											handleChange('dioceseId', val?.id);
											setDioceseName(val?.label);
										}}
										options={(searchDiocese?.data || []).map((d: Diocese) => ({
											id: d.id,
											label: `${d.dioceseName}`,
										}))}
										renderInput={(params) => (
											<Stack alignItems="center" direction="row" spacing={1}>
												<TextField
													{...params}
													variant="outlined"
													label=""
													InputProps={{
														...params.InputProps,
													}}
													onChange={(event: any) => {
														setDioceseSearchTerm(event.target.value);
													}}
												/>

												<IconButton
													size="small"
													onClick={() => handleVisibleSearchDiocese()}
													sx={{ borderRadius: '5px', border: '1px solid var(--mainColor)' }}
												>
													<Search color="primary" />
												</IconButton>
											</Stack>
										)}
									/>
								</Stack>
								{formErrors.includes('dioceseId') && (
									<span className={global.errorText}>Diocese is required</span>
								)}
							</Grid>
						</Grid>
						<Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2 }}>Contact Detail</Typography>
						<Divider />

						<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<AccountCircle className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Contact Name
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Contact Name"
									value={initial.contactName}
									onChange={(e) => {
										handleChange('contactName', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Phone className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Phone
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Phone"
									value={initial.phone}
									type="number"
									onChange={(e) => {
										handleChange('phone', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Phone className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Fax
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Fax"
									value={initial.fax}
									onChange={(e) => {
										handleChange('fax', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Email className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Email
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Email"
									value={initial.email}
									onChange={(e) => {
										handleChange('email', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<ConfirmationNumber className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Second Language
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Second Language"
									value={initial.secondLanguage}
									onChange={(e) => {
										handleChange('secondLanguage', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<FormControlLabel
									name="useEFile"
									value="top"
									control={
										<Checkbox
											checked={initial.useEFile}
											onChange={(e) => handleChange('useEFile', e.target.checked)}
										/>
									}
									label="Use EFile"
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<FormControlLabel
									name="useEmail"
									value="top"
									control={
										<Checkbox
											checked={initial.useEmail}
											onChange={(e) => handleChange('useEmail', e.target.checked)}
										/>
									}
									label="Use Email"
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<FormControlLabel
									name="magazine"
									value="top"
									control={
										<Checkbox
											checked={initial.magazine}
											onChange={(e) => handleChange('magazine', e.target.checked)}
										/>
									}
									label="Magazine"
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<FormControlLabel
									name="noParishList"
									value="top"
									control={
										<Checkbox
											checked={initial.noParishList}
											onChange={(e) => handleChange('noParishList', e.target.checked)}
										/>
									}
									label="No Parish List"
								/>
							</Grid>
						</Grid>

						<Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2 }}>Address Detail</Typography>
						<Divider />

						<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<LocationOn className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Streetline 1
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Streetline 1"
									value={initial.streetline1}
									onChange={(e) => {
										handleChange('streetline1', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<MyLocation className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Streetline 2
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Streetline 2"
									value={initial.streetline2}
									onChange={(e) => {
										handleChange('streetline2', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<LocationCity className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											City
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="City"
									value={initial.city}
									onChange={(e) => {
										handleChange('city', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<AssistantDirection className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Province
										</Typography>
									</Grid>
								</Grid>
								<ProvinceAutocomplete
									value={initial.province}
									handleChange={(val) => handleChange('provinceId', parseInt(val?.id))}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<Markunread className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Postal Code
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Postal Code"
									value={initial.postalCode}
									onChange={(e) => {
										handleChange('postalCode', e.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
								<Grid container>
									<Grid item>
										<LocationCity className={global.formLabelIcon} />
									</Grid>
									<Grid item>
										<Typography variant="body2" className={global.formLabelText}>
											Country
										</Typography>
									</Grid>
								</Grid>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Country"
									value={initial.country}
									onChange={(e) => {
										handleChange('country', e.target.value);
									}}
								/>
							</Grid>
						</Grid>
					</>
				)}
				{visibleSearchDiocese && (
					<AdvancedSearchModal
						visible={visibleSearchDiocese}
						handleClose={handleVisibleSearchDiocese}
						field={searchFieldsDiocese}
						setField={setSearchFieldsDiocese}
						title={'Search Diocese'}
						handleSearch={handleSearchDiocese}
					>
						<>
							<Stack alignItems={'flex-end'}>
								<TrimTables
									name="dioceseHead"
									list={dioceseHead}
									trimList={trimList}
									setTrimList={setTrimList}
								/>
							</Stack>
							<DioceseTable
								trimList={trimList}
								setTrimList={setTrimList}
								page={page}
								pageSize={pageSize}
								setPage={setPage}
								setPageSize={setPageSize}
								label={label}
								orderType={orderType}
								setLabel={setLabel}
								setOderBy={setOderBy}
								setOrderType={setOrderType}
								dioceses={dioceses}
								hcaOrgList
								open={open}
								setOpen={setOpen}
								handleOpenEdit={handleSelectRowDiocese}
							/>
						</>
					</AdvancedSearchModal>
				)}
			</Stack>
		</>
	);
};

export default CreateEditParish;
